/**
 * `useProofCheck` — ADR-0050 §1.2 (Shape B) extraction of the proof-
 * token load + proof-check call previously embedded in
 * `ProofStatusPanel`. The wire contract is unchanged from ADR-0043:
 *
 *   - `GET /v1/sites/:site_id/proof-token` (credentials: include) on
 *     mount and on every reload nonce bump.
 *   - `POST /v1/sites/:site_id/proof-check` (credentials: include) when
 *     {@link UseProofCheckState.verify} fires.
 *   - The `safe-fetch` origin-side machinery, attribute-match rule,
 *     constant-time compare, rate limit, `pending_proof` gate — all
 *     unchanged. This hook is presentation-layer composition only.
 *
 * Consumed by:
 *   - `ScriptTagInstallPanel` — to thread the `proof_token` into
 *     `buildScriptSnippet(site_key, proof_token)` and to render the
 *     inline Verify button next to the snippet.
 *   - `ProofStatusPanel` — to render the post-verify status, the
 *     `.well-known` fallback affordance, and the loading / error
 *     states. The snippet textarea is no longer rendered there
 *     (ADR-0050 §1.2 — paste-once / verify-once).
 *
 * Both consumers receive the SAME hook state in the same render tree
 * (`InstallVerifyStep` is the single caller); the hook itself does no
 * de-duplication of the underlying GET — the consumer is responsible
 * for mounting it once per Step 3 instance.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Site, SiteId } from '@sill/types';
import type { DetectPlatformResult } from '@sill/types/api';

import { useSites, useToast } from '../app/providers.js';
import { API_ORIGIN } from '../app/env.js';

export interface ProofTokenData {
  proof_token: string;
  install_snippet: string;
  fallback: {
    well_known_url: string;
    well_known_body: string;
  };
  proof_status: 'pending' | 'recorded' | 'rotated_pending';
  recorded_at: string | null;
}

interface ProofCheckResponse {
  ok: boolean;
  proof_path?: 'snippet_in_homepage' | 'well_known';
  reason?: string;
  error?: string;
  /** safeFetch abort class from the server (e.g. `redirect_refused`) —
   *  rides alongside `reason: 'fetch_failed'` so the friendly mapping
   *  can distinguish "site is down" from "site redirects" (founder
   *  live-testing 2026-07-08: a www-canonical WordPress site got the
   *  misleading "couldn't reach" copy). */
  fetch_reason?: string;
  homepage_status?: number;
  well_known_status?: number;
  recorded_at?: string;
}

/** Friendly-message mapping (preserved verbatim from ProofStatusPanel). */
export interface VerifyFailureMessage {
  title: string;
  body: string;
  tone: 'amber' | 'coral';
  show_fallback: boolean;
  /**
   * ADR-0052 §2 — when set, the consuming surface renders an inline
   * "Connect Shopify" CTA next to the friendly message. Used by the
   * Shopify-password-page branch to steer the merchant to OAuth-as-
   * proof. The proof primitive itself is unchanged — this is a UX
   * label on an already-failing match; password pages are NEVER
   * treated as proof.
   */
  show_oauth_cta?: boolean;
}

/**
 * ADR-0052 §2.4 — optional context for the friendly-message mapping.
 * Currently used only to detect a Shopify-detected site so the
 * homepage-miss branch can surface the password-page friendly miss
 * with an OAuth CTA. Adding context is intentionally additive — the
 * proof-check wire contract (route + response shape) is unchanged.
 */
export interface VerifyFailureContext {
  readonly detected_platform?: DetectPlatformResult['platform'] | null;
  /** The server's safeFetch abort class, when `reason` is
   *  `fetch_failed` (see ProofCheckResponse.fetch_reason). */
  readonly fetch_reason?: string | undefined;
}

export function friendlyVerifyMessage(
  status: number,
  reason: string | undefined,
  domain: string,
  ctx?: VerifyFailureContext,
): VerifyFailureMessage {
  if (reason === 'homepage_miss_and_well_known_mismatch') {
    // ADR-0052 §2.1 — on a detected-Shopify site the most common cause
    // of this exact miss is a password-protected (Opening soon) dev
    // store; the snippet ISN'T on the served HTML because the served
    // HTML is Shopify's password page. Surface that + steer to OAuth.
    // The match logic in `proof-html-match.ts` is unchanged: the match
    // has already failed, no proof row was written, the site is still
    // `pending_proof`. The only thing changing is the human-readable
    // label and the CTA we offer next to it.
    if (ctx?.detected_platform === 'shopify') {
      return {
        title: 'Store is password-protected',
        body: `${domain} is showing Shopify's password page. Sill can only see what agents would see, so the snippet isn't on the served HTML. You have two ways forward: remove the store password and click Verify again, or connect Shopify — Shopify confirms your verified domain to us.`,
        tone: 'amber',
        show_fallback: false,
        show_oauth_cta: true,
      };
    }
    return {
      title: 'Snippet not found yet',
      body: `We fetched ${domain} but couldn't find the Sill snippet in the page. Paste it into your site's HTML and publish, then click Verify again.`,
      tone: 'amber',
      show_fallback: true,
    };
  }
  if (
    reason === 'fetch_failed'
    || reason === 'homepage_miss_and_well_known_unreachable'
  ) {
    // 2026-07-08 founder live-testing — a site whose canonical URL
    // redirects (apex → www is the classic WordPress setup) got the
    // generic "couldn't reach" copy, which reads as an outage on a
    // perfectly healthy site. Sill never follows redirects when
    // verifying ownership (the anti-spoofing resolve-then-pin rule),
    // so name the actual situation and both ways out.
    if (ctx?.fetch_reason === 'redirect_refused') {
      const www_hint = domain.startsWith('www.')
        ? domain.replace(/^www\./, '')
        : `www.${domain}`;
      return {
        title: `${domain} redirects somewhere else`,
        body: `${domain} answers with a redirect — usually to ${www_hint}. For ownership proof Sill checks the exact domain you registered and never follows redirects. Either add the domain your site actually serves (likely ${www_hint}) as the site instead, or change your site to serve ${domain} directly.`,
        tone: 'amber',
        show_fallback: false,
      };
    }
    return {
      title: `Couldn't reach ${domain}`,
      body: `We couldn't load ${domain} to check it. Make sure the site is live and publicly reachable over HTTPS, then try again.`,
      tone: 'amber',
      show_fallback: false,
    };
  }
  if (reason === 'domain_claimed_by_another_account') {
    return {
      title: 'Domain already verified elsewhere',
      body: `${domain} is already verified on another Sill account. If you own this domain and need to move it, the current owner can remove it from their account and you can verify it here.`,
      tone: 'amber',
      show_fallback: false,
    };
  }
  if (status === 403) {
    return {
      title: 'Site unavailable',
      body: "This site is suspended and can't be verified.",
      tone: 'coral',
      show_fallback: false,
    };
  }
  if (status === 404) {
    return {
      title: 'Site not found',
      body: "We couldn't find this site. Reload the page and try again.",
      tone: 'coral',
      show_fallback: false,
    };
  }
  if (status === 429) {
    return {
      title: 'Too many attempts',
      body: "You've tried several times in a row. Wait a minute, then verify again.",
      tone: 'amber',
      show_fallback: false,
    };
  }
  return {
    title: "Couldn't verify yet",
    body: `We couldn't check ${domain} just now. Confirm the snippet is installed and the domain is correct, then try again.`,
    tone: 'amber',
    show_fallback: true,
  };
}

export interface UseProofCheckState {
  /** Active site the hook is bound to. Null when no site is mounted. */
  site: Site | null;
  /** Resolved proof-token + .well-known fallback payload. Null until
   *  the first load resolves OR on a load error (see {@link loadError}). */
  data: ProofTokenData | null;
  loading: boolean;
  loadError: string | null;
  /** True while the in-flight `proof-check` POST is pending. */
  checking: boolean;
  /** True once the proof-check has succeeded OR the site is already in
   *  a verified status (`discovery_active`/`transactional_active`). */
  verified: boolean;
  /** True after a failed verify that the friendly-message mapping
   *  classifies as `show_fallback`. The consumer renders the `.well-
   *  known` fallback block when this flips. */
  showFallback: boolean;
  /** ADR-0052 §2 — the last friendly message produced by a failed
   *  verify. Consumers render the title + body + optional
   *  `show_oauth_cta` from it (the password-protected Shopify-store
   *  branch sets `show_oauth_cta=true` so the consumer can render an
   *  inline Connect Shopify affordance). Null until the first failure. */
  lastFailure: VerifyFailureMessage | null;
  /** Fire the proof-check. Resolves silently regardless of outcome —
   *  the toast surface and `verified` state convey the result. */
  verify: () => Promise<void>;
  /** Force a re-load of the proof-token (e.g. after a key rotation). */
  reload: () => void;
}

export interface UseProofCheckOptions {
  /** API base URL override — defaults to `API_ORIGIN`. */
  apiBaseUrl?: string;
  /** Fires once on a successful verify (status flip → discovery_active). */
  onVerified?: () => void;
  /** When `false`, the hook does NOT fire its `GET /proof-token` load
   *  and returns a dormant state object (loading=false, data=null,
   *  verify=no-op). Used by `ProofStatusPanel` to avoid double-mounting
   *  the hook when a parent already mounted it and passes its state in
   *  via props (ADR-0050 §1.2). Default `true`. */
  enabled?: boolean;
  /** ADR-0052 §2.4 — the merchant's currently-detected platform,
   *  threaded into `friendlyVerifyMessage` so a homepage-miss on a
   *  Shopify-detected site surfaces the password-page friendly miss +
   *  the Connect Shopify CTA. Null / undefined preserves the
   *  pre-ADR-0052 behavior. The verify() call reads this on every
   *  invocation so a late-arriving detection result takes effect on
   *  the next click without a re-mount. */
  detectedPlatform?: DetectPlatformResult['platform'] | null;
}

export function useProofCheck(
  options: UseProofCheckOptions = {},
): UseProofCheckState {
  const toast = useToast();
  const { activeSite, updateSite, setActiveSite } = useSites();
  const [data, setData] = useState<ProofTokenData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [checking, setChecking] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [lastFailure, setLastFailure] = useState<VerifyFailureMessage | null>(
    null,
  );

  const enabled = options.enabled !== false;
  const base = options.apiBaseUrl ?? API_ORIGIN;
  const detectedPlatform = options.detectedPlatform ?? null;

  const verified = useMemo<boolean>(() => {
    if (activeSite?.status === 'discovery_active') return true;
    if (activeSite?.status === 'transactional_active') return true;
    if (data?.proof_status === 'recorded') return true;
    return false;
  }, [activeSite?.status, data?.proof_status]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (activeSite === null) {
      setLoading(false);
      return undefined;
    }
    const site_id: SiteId = activeSite.site_id;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void fetch(`${base}/v1/sites/${site_id}/proof-token`, {
      credentials: 'include',
    })
      .then((r): Promise<ProofTokenData> => {
        if (!r.ok) throw new Error(`proof_token_${r.status}`);
        return r.json() as Promise<ProofTokenData>;
      })
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setLoadError(msg);
        toast({
          title: 'Could not load proof token',
          body: msg,
          tone: 'coral',
        });
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return (): void => {
      cancelled = true;
    };
  }, [activeSite, base, enabled, toast, reloadNonce]);

  const verify = useCallback(async (): Promise<void> => {
    if (activeSite === null || checking) return;
    setChecking(true);
    try {
      const resp = await fetch(
        `${base}/v1/sites/${activeSite.site_id}/proof-check`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({}),
        },
      );
      let payload: ProofCheckResponse = { ok: false };
      try {
        payload = (await resp.json()) as ProofCheckResponse;
      } catch {
        /* non-JSON body — fall through to status-based mapping */
      }

      if (resp.ok && payload.ok) {
        toast({
          title: `${activeSite.domain} verified`,
          body:
            payload.proof_path === 'well_known'
              ? 'Proof recorded via the .well-known fallback.'
              : 'Proof recorded via the homepage snippet.',
          tone: 'lime',
        });
        // The server returned `ok: true` only AFTER writing the proof
        // row and flipping the row status under its tenant tx. This is a
        // zero-latency UI CACHE of a status the server already granted —
        // NOT a client-side grant of authority. The advance fast-path
        // and any gate that reads `site.status` stay sound because the
        // real transition happened server-side first.
        const next_site = {
          ...activeSite,
          status: 'discovery_active' as const,
        };
        updateSite(activeSite.site_id, next_site);
        setActiveSite(next_site);
        setLastFailure(null);
        options.onVerified?.();
        return;
      }

      const m = friendlyVerifyMessage(
        resp.status,
        payload.reason ?? payload.error,
        activeSite.domain,
        {
          detected_platform: detectedPlatform,
          fetch_reason: payload.fetch_reason,
        },
      );
      if (m.show_fallback) setShowFallback(true);
      setLastFailure(m);
      toast({ title: m.title, body: m.body, tone: m.tone });
    } catch (err) {
      // 2026-10-29 (netalinissim.com) — a request that never reaches
      // Sill surfaced the browser's raw "Failed to fetch". The toast has
      // no action slot; the Verify button stays enabled as the retry.
      // eslint-disable-next-line no-console
      console.debug('proof-check fetch failed', err instanceof Error ? err.name : typeof err);
      toast({
        title: "Couldn't reach Sill",
        body: 'Check your connection and try again in a moment.',
        tone: 'amber',
      });
    } finally {
      setChecking(false);
    }
  }, [
    activeSite,
    base,
    checking,
    detectedPlatform,
    options,
    setActiveSite,
    toast,
    updateSite,
  ]);

  const reload = useCallback((): void => {
    setReloadNonce((n) => n + 1);
  }, []);

  return {
    site: activeSite,
    data,
    loading,
    loadError,
    checking,
    verified,
    showFallback,
    lastFailure,
    verify,
    reload,
  };
}
